import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Logo } from './Logo';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  return (
    <footer className="bg-dark-900 overflow-x-hidden" role="contentinfo">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-6 sm:py-8 md:py-10 lg:py-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
          {/* Company Info */}
          <div className="col-span-2 lg:col-span-1 space-y-3 sm:space-y-4">
            <Logo />
            <p className="text-gray-300 text-xs sm:text-sm leading-relaxed max-w-xs">
              {t('landing.footer.description')}
            </p>
          </div>

          {/* Quick Links */}
          <nav aria-label="Quick links">
            <h3 className="text-white font-semibold mb-2 sm:mb-3 md:mb-4 text-xs sm:text-sm md:text-base">{t('footer.quickLinks')}</h3>
            <ul className="space-y-1 sm:space-y-2">
              <li><button onClick={() => handleNavigation('/')} className="text-gray-300 hover:text-primary-400 transition-colors duration-200 text-[10px] sm:text-xs md:text-sm block py-0.5 sm:py-1 text-left">{t('navigation.home')}</button></li>
              <li><button onClick={() => handleNavigation('/sports')} className="text-gray-300 hover:text-primary-400 transition-colors duration-200 text-[10px] sm:text-xs md:text-sm block py-0.5 sm:py-1 text-left">{t('navigation.sports')}</button></li>
              <li><button onClick={() => handleNavigation('/live-sports')} className="text-gray-300 hover:text-primary-400 transition-colors duration-200 text-[10px] sm:text-xs md:text-sm block py-0.5 sm:py-1 text-left">{t('notice.liveSports')}</button></li>
              <li><button onClick={() => handleNavigation('/slot')} className="text-gray-300 hover:text-primary-400 transition-colors duration-200 text-[10px] sm:text-xs md:text-sm block py-0.5 sm:py-1 text-left">{t('navigation.slotGames')}</button></li>
              <li><button onClick={() => handleNavigation('/casino')} className="text-gray-300 hover:text-primary-400 transition-colors duration-200 text-[10px] sm:text-xs md:text-sm block py-0.5 sm:py-1 text-left">{t('navigation.liveCasino')}</button></li>
              <li><button onClick={() => handleNavigation('/minigame')} className="text-gray-300 hover:text-primary-400 transition-colors duration-200 text-[10px] sm:text-xs md:text-sm block py-0.5 sm:py-1 text-left">{t('navigation.minigame')}</button></li>
              <li><button onClick={() => handleNavigation('/bonus')} className="text-gray-300 hover:text-primary-400 transition-colors duration-200 text-[10px] sm:text-xs md:text-sm block py-0.5 sm:py-1 text-left">{t('navigation.bonus')}</button></li>
              <li><button onClick={() => handleNavigation('/rule')} className="text-gray-300 hover:text-primary-400 transition-colors duration-200 text-[10px] sm:text-xs md:text-sm block py-0.5 sm:py-1 text-left">{t('navigation.about')}</button></li>
            </ul>
          </nav>

          {/* Support */}
          <nav aria-label="Support links">
            <h3 className="text-white font-semibold mb-2 sm:mb-3 md:mb-4 text-xs sm:text-sm md:text-base">{t('landing.footer.support')}</h3>
            <ul className="space-y-1 sm:space-y-2">
              <li><button onClick={() => handleNavigation('/message')} className="text-gray-300 hover:text-primary-400 transition-colors duration-200 text-[10px] sm:text-xs md:text-sm block py-0.5 sm:py-1 text-left">{t('landing.footer.contactUs')}</button></li>
              <li><button onClick={() => handleNavigation('/rule')} className="text-gray-300 hover:text-primary-400 transition-colors duration-200 text-[10px] sm:text-xs md:text-sm block py-0.5 sm:py-1 text-left">{t('landing.footer.responsibleGambling')}</button></li>
            </ul>
          </nav>

          {/* Legal */}
          <nav aria-label="Legal links">
            <h3 className="text-white font-semibold mb-2 sm:mb-3 md:mb-4 text-xs sm:text-sm md:text-base">{t('landing.footer.legal')}</h3>
            <ul className="space-y-1 sm:space-y-2">
              <li><button onClick={() => handleNavigation('/rule')} className="text-gray-300 hover:text-primary-400 transition-colors duration-200 text-[10px] sm:text-xs md:text-sm block py-0.5 sm:py-1 text-left">{t('landing.footer.termsConditions')}</button></li>
            </ul>
          </nav>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-dark-700 mt-6 sm:mt-8 md:mt-10 lg:mt-12 pt-4 sm:pt-6 md:pt-8">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <div className="text-gray-300 text-[10px] sm:text-xs md:text-sm text-center sm:text-left max-w-md">
              <p>&copy; {new Date().getFullYear()} ApexBet. {t('landing.footer.copyright')}</p>
            </div>
            <div className="flex items-center space-x-3 sm:space-x-4 md:space-x-6">
              <div className="flex items-center space-x-1 sm:space-x-2">
                <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-500 rounded-md sm:rounded-lg flex items-center justify-center" aria-hidden="true">
                  <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <span className="text-gray-300 text-[10px] sm:text-xs md:text-sm">{t('landing.footer.sslSecured')}</span>
              </div>
              <div className="flex items-center space-x-1 sm:space-x-2">
                <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-500 rounded-md sm:rounded-lg flex items-center justify-center" aria-hidden="true">
                  <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                  </svg>
                </div>
                <span className="text-gray-300 text-[10px] sm:text-xs md:text-sm">{t('landing.footer.licensed')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 